import type { AuthManager } from "./authManager";
import type { GamePhase } from "../game/types";
import { CANVAS_HEIGHT, CANVAS_WIDTH } from "../game/constants";
import { isMobileViewport } from "../game/platform";
import { resolveAuthGateLayout } from "./authLayout";

export class AuthOverlay {
  private authManager: AuthManager;
  private container: HTMLDivElement;
  private form: HTMLDivElement;
  private emailInput: HTMLInputElement;
  private passwordInput: HTMLInputElement;
  private errorDiv: HTMLDivElement;
  private submitBtn: HTMLButtonElement;
  private toggleBtn: HTMLButtonElement;
  private confirmDiv: HTMLDivElement;
  private mode: "signin" | "signup" = "signin";
  private busy = false;
  private visible = false;
  private resizeObserver: ResizeObserver | null = null;

  constructor(authManager: AuthManager) {
    this.authManager = authManager;

    // Container — absolute overlay, no pointer events on wrapper
    this.container = document.createElement("div");
    this.container.className = "auth-overlay";
    this.container.style.display = "none";

    // Form — pointer events enabled
    this.form = document.createElement("div");
    this.form.className = "auth-form";

    this.emailInput = document.createElement("input");
    this.emailInput.type = "email";
    this.emailInput.placeholder = "EMAIL";
    this.emailInput.className = "auth-input";
    this.emailInput.autocomplete = "email";

    this.passwordInput = document.createElement("input");
    this.passwordInput.type = "password";
    this.passwordInput.placeholder = "PASSWORD";
    this.passwordInput.className = "auth-input";
    this.passwordInput.autocomplete = "current-password";

    this.errorDiv = document.createElement("div");
    this.errorDiv.className = "auth-error";

    this.confirmDiv = document.createElement("div");
    this.confirmDiv.className = "auth-confirm";

    this.submitBtn = document.createElement("button");
    this.submitBtn.className = "auth-btn-submit";
    this.submitBtn.textContent = "SIGN IN";

    this.toggleBtn = document.createElement("button");
    this.toggleBtn.className = "auth-btn-toggle";
    this.toggleBtn.textContent = "NO ACCOUNT? SIGN UP";

    this.form.appendChild(this.emailInput);
    this.form.appendChild(this.passwordInput);
    this.form.appendChild(this.errorDiv);
    this.form.appendChild(this.confirmDiv);
    this.form.appendChild(this.submitBtn);
    this.form.appendChild(this.toggleBtn);
    this.container.appendChild(this.form);

    this.submitBtn.addEventListener("click", () => void this.handleSubmit());
    this.toggleBtn.addEventListener("click", () => this.toggleMode());

    // Allow Enter in password field to submit
    this.passwordInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void this.handleSubmit();
      }
    });
    // Allow Enter in email field to focus password
    this.emailInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.passwordInput.focus({ preventScroll: true });
      }
    });
  }

  attach(wrapper: HTMLElement): void {
    wrapper.appendChild(this.container);

    this.resizeObserver = new ResizeObserver(() => this.rescaleFont(wrapper));
    this.resizeObserver.observe(wrapper);
    this.rescaleFont(wrapper);
  }

  syncPhase(phase: GamePhase): void {
    const shouldShow = phase === "AUTH_GATE";
    if (shouldShow === this.visible) return;
    this.visible = shouldShow;
    this.container.style.display = shouldShow ? "" : "none";
    if (!shouldShow) {
      // Reset state when hidden
      this.errorDiv.textContent = "";
      this.confirmDiv.textContent = "";
      this.emailInput.value = "";
      this.passwordInput.value = "";
    }
  }

  private rescaleFont(wrapper: HTMLElement): void {
    const canvasEl = wrapper.querySelector("canvas");
    const refWidth = canvasEl?.clientWidth ?? wrapper.clientWidth;
    const refHeight = canvasEl?.clientHeight ?? wrapper.clientHeight;

    let scale = 1;
    if (refWidth > 0 && refHeight > 0) {
      scale = Math.min(refWidth / CANVAS_WIDTH, refHeight / CANVAS_HEIGHT);
    } else if (refWidth > 0) {
      scale = refWidth / CANVAS_WIDTH;
    } else if (refHeight > 0) {
      scale = refHeight / CANVAS_HEIGHT;
    }

    const baseFontSize = Math.max(4, scale * 10);
    this.container.style.fontSize = `${baseFontSize}px`;

    // Use authLayout to position form at the correct canvas-relative Y
    const mobile = isMobileViewport();
    const layout = resolveAuthGateLayout({
      canvasWidth: CANVAS_WIDTH,
      canvasHeight: CANVAS_HEIGHT,
      mobile
    });
    const formTopPx = layout.emailFormRect.y * scale;
    this.form.style.marginTop = `${formTopPx}px`;

    // Match form width to canvas OAuth button width (280px mobile, 196px desktop)
    const canvasLeft = canvasEl ? canvasEl.offsetLeft : 0;
    const canvasW = canvasEl?.clientWidth ?? refWidth;
    const formWidthPx = (mobile ? 280 : 196) * scale;
    this.form.style.width = `${formWidthPx}px`;
    this.form.style.marginLeft = `${canvasLeft + (canvasW - formWidthPx) / 2}px`;
    this.form.style.marginRight = "0";

    // Match element heights to canvas button height (44px mobile, 36px desktop)
    const btnHeightPx = (mobile ? 44 : 36) * scale;
    const btnFontPx = Math.max(4, (mobile ? 9 : 7) * scale);
    this.emailInput.style.height = `${btnHeightPx}px`;
    this.emailInput.style.fontSize = `${btnFontPx}px`;
    this.passwordInput.style.height = `${btnHeightPx}px`;
    this.passwordInput.style.fontSize = `${btnFontPx}px`;
    this.submitBtn.style.height = `${btnHeightPx}px`;
    this.submitBtn.style.fontSize = `${btnFontPx}px`;
    this.toggleBtn.style.fontSize = `${Math.max(4, (mobile ? 7 : 5) * scale)}px`;
  }

  private async handleSubmit(): Promise<void> {
    if (this.busy) return;

    const email = this.emailInput.value.trim();
    const password = this.passwordInput.value;
    this.errorDiv.textContent = "";
    this.confirmDiv.textContent = "";

    if (!email || !password) {
      this.errorDiv.textContent = "ENTER EMAIL AND PASSWORD";
      return;
    }

    this.busy = true;
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = "...";

    let result: { error: string | null };
    if (this.mode === "signin") {
      result = await this.authManager.signInWithEmail(email, password);
    } else {
      result = await this.authManager.signUpWithEmail(email, password);
    }

    this.busy = false;
    this.submitBtn.disabled = false;
    this.submitBtn.textContent = this.mode === "signin" ? "SIGN IN" : "SIGN UP";

    if (result.error) {
      this.errorDiv.textContent = result.error;
    } else if (this.mode === "signup") {
      // signUp with no error but auth listener hasn't fired → confirmation needed
      const authState = this.authManager.getState();
      if (!authState.user) {
        this.confirmDiv.textContent = "CHECK EMAIL TO CONFIRM";
      }
    }
  }

  private toggleMode(): void {
    this.mode = this.mode === "signin" ? "signup" : "signin";
    this.errorDiv.textContent = "";
    this.confirmDiv.textContent = "";
    this.submitBtn.textContent = this.mode === "signin" ? "SIGN IN" : "SIGN UP";
    this.toggleBtn.textContent =
      this.mode === "signin" ? "NO ACCOUNT? SIGN UP" : "HAVE ACCOUNT? SIGN IN";
    this.passwordInput.autocomplete =
      this.mode === "signin" ? "current-password" : "new-password";
  }
}
